import { useState } from "react";
import { apiCall } from "./api";
import { FONT_BODY, FONT_DISPLAY } from "./theme";

// ============================================================
// BLACK OWL - Feedback Panel
// Users can send bugs, feedback, and feature requests to devs
// ============================================================

export default function FeedbackPanel({ username, lobbyId, currentScreen, onClose }) {
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    if (!message.trim()) {
      setError("Please describe your feedback.");
      return;
    }
    if (message.trim().length < 10) {
      setError("Please provide a bit more detail (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      await apiCall("submit_feedback", {
        username: username || "anonymous",
        category,
        message: message.trim(),
        contact: contact.trim(),
        screen: currentScreen || "unknown",
        lobby_id: lobbyId || null,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
      setSubmitted(true);
      setMessage("");
      setContact("");
    } catch (err) {
      setError(err.message || "Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const categories = [
    { key: "bug", label: "🐞 Bug", desc: "Something isn't working right" },
    { key: "feature", label: "💡 Idea", desc: "Suggest a new feature" },
    { key: "feedback", label: "💬 Feedback", desc: "General thoughts" },
    { key: "other", label: "📝 Other", desc: "Anything else" },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(8px)",
        }}
      />

      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "440px", maxHeight: "85vh",
        margin: "20px",
        background: "rgba(12, 12, 22, 0.95)",
        border: "1px solid rgba(201, 162, 39, 0.2)",
        borderRadius: "16px",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid rgba(201, 162, 39, 0.1)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{
              fontSize: "18px", fontWeight: 700, color: "#c9a227",
              margin: 0, letterSpacing: "3px", textTransform: "uppercase",
              fontFamily: FONT_DISPLAY,
            }}>Send Feedback</h2>
            <p style={{
              fontSize: "11px", color: "#5a5a6e", margin: "4px 0 0",
              fontFamily: FONT_BODY,
            }}>Help us make Black Owl better</p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px", height: "32px",
              fontSize: "18px", color: "#5a5a6e",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "8px", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>
        </div>

        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
              <p style={{
                fontSize: "16px", fontWeight: 700, color: "#32c850",
                margin: "0 0 8px", fontFamily: FONT_BODY,
                letterSpacing: "2px", textTransform: "uppercase",
              }}>Thanks!</p>
              <p style={{
                fontSize: "13px", color: "#a0a0b0", margin: "0 0 24px",
                fontFamily: FONT_BODY,
              }}>Your feedback has been received. We appreciate you taking the time to help improve the game.</p>
              <button
                onClick={() => setSubmitted(false)}
                style={{
                  padding: "10px 20px", fontSize: "11px",
                  fontWeight: 600, letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  fontFamily: FONT_BODY,
                  color: "#c9a227", background: "rgba(201, 162, 39, 0.08)",
                  border: "1px solid rgba(201, 162, 39, 0.2)",
                  borderRadius: "8px", cursor: "pointer",
                }}
              >Send Another</button>
            </div>
          ) : (
            <>
              <label style={{
                display: "block", fontSize: "11px", fontWeight: 600,
                letterSpacing: "2px", textTransform: "uppercase",
                color: "#7a7a8e", marginBottom: "8px",
                fontFamily: FONT_BODY,
              }}>What is this?</label>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px",
                marginBottom: "20px",
              }}>
                {categories.map(({ key, label, desc }) => (
                  <button
                    key={key}
                    onClick={() => setCategory(key)}
                    style={{
                      padding: "12px",
                      fontSize: "12px", fontWeight: 600,
                      fontFamily: FONT_BODY,
                      color: category === key ? "#0a0a14" : "#a0a0b0",
                      background: category === key ? "#c9a227" : "rgba(255,255,255,0.03)",
                      border: `1.5px solid ${category === key ? "#c9a227" : "rgba(255,255,255,0.08)"}`,
                      borderRadius: "8px", cursor: "pointer",
                      transition: "all 0.25s ease", textAlign: "left",
                    }}
                  >
                    <div style={{ marginBottom: "2px", fontWeight: 700 }}>{label}</div>
                    <div style={{
                      fontSize: "10px",
                      color: category === key ? "rgba(10,10,20,0.7)" : "#5a5a6e",
                    }}>{desc}</div>
                  </button>
                ))}
              </div>

              <label style={{
                display: "block", fontSize: "11px", fontWeight: 600,
                letterSpacing: "2px", textTransform: "uppercase",
                color: "#7a7a8e", marginBottom: "8px",
                fontFamily: FONT_BODY,
              }}>Your message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  category === "bug"
                    ? "Describe what went wrong. What did you expect vs what happened?"
                    : category === "feature"
                    ? "Tell us your idea!"
                    : "Share your thoughts..."
                }
                rows={5}
                style={{
                  width: "100%", padding: "12px 14px",
                  fontSize: "13px", fontFamily: FONT_BODY,
                  color: "#e8e6df", background: "rgba(255,255,255,0.03)",
                  border: "1.5px solid rgba(255,255,255,0.08)",
                  borderRadius: "8px", outline: "none",
                  boxSizing: "border-box", resize: "vertical",
                  marginBottom: "16px",
                }}
              />

              <label style={{
                display: "block", fontSize: "11px", fontWeight: 600,
                letterSpacing: "2px", textTransform: "uppercase",
                color: "#7a7a8e", marginBottom: "8px",
                fontFamily: FONT_BODY,
              }}>Email (optional)</label>
              <input
                type="email"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="If you want a reply"
                style={{
                  width: "100%", padding: "12px 14px",
                  fontSize: "13px", fontFamily: FONT_BODY,
                  color: "#e8e6df", background: "rgba(255,255,255,0.03)",
                  border: "1.5px solid rgba(255,255,255,0.08)",
                  borderRadius: "8px", outline: "none",
                  boxSizing: "border-box", marginBottom: "16px",
                }}
              />

              {error && (
                <div style={{
                  padding: "12px 16px", marginBottom: "16px",
                  background: "rgba(220, 50, 50, 0.1)",
                  border: "1px solid rgba(220, 50, 50, 0.25)",
                  borderRadius: "8px", color: "#e85555",
                  fontSize: "13px", fontFamily: FONT_BODY,
                }}>{error}</div>
              )}

              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: "100%", padding: "14px",
                  fontSize: "13px", fontWeight: 700,
                  letterSpacing: "2px", textTransform: "uppercase",
                  fontFamily: FONT_BODY,
                  color: "#0a0a14",
                  background: submitting
                    ? "rgba(201, 162, 39, 0.5)"
                    : "linear-gradient(135deg, #c9a227 0%, #e0be4a 50%, #c9a227 100%)",
                  border: "none", borderRadius: "8px",
                  cursor: submitting ? "wait" : "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 20px rgba(201, 162, 39, 0.2)",
                }}
              >
                {submitting ? "Sending..." : "Send Feedback"}
              </button>

              <p style={{
                fontSize: "10px", color: "#3a3a4e", margin: "12px 0 0",
                fontFamily: FONT_BODY, fontStyle: "italic",
                textAlign: "center",
              }}>
                {username && `Sending as ${username}. `}
                Context (screen, lobby, browser) is included automatically.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function FeedbackButton({ onClick, variant = "default" }) {
  if (variant === "icon") {
    return (
      <button
        onClick={onClick}
        title="Send feedback"
        style={{
          padding: "8px 10px",
          fontSize: "14px",
          fontFamily: FONT_BODY,
          color: "#c9a227",
          background: "rgba(201, 162, 39, 0.06)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "8px", cursor: "pointer",
          transition: "all 0.2s ease",
        }}
      >💬</button>
    );
  }
  return (
    <button
      onClick={onClick}
      style={{
        padding: "10px 18px",
        fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px",
        textTransform: "uppercase",
        fontFamily: FONT_BODY,
        color: "#c9a227",
        background: "rgba(201, 162, 39, 0.06)",
        border: "1px solid rgba(201, 162, 39, 0.15)",
        borderRadius: "8px", cursor: "pointer",
        transition: "all 0.2s ease",
        display: "inline-flex", alignItems: "center", gap: "8px",
      }}
    >
      <span style={{ fontSize: "14px" }}>💬</span> Send Feedback
    </button>
  );
}
